import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import arcaLogo from '../assets/arca_logo.png';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import PublicLayout from '../components/public/PublicLayout';

const PasswordSetup = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'
  const [token, setToken] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    // Get token from URL parameters
    const urlToken = searchParams.get('token');
    const userType = searchParams.get('type');
    
    if (urlToken) {
      setToken(urlToken);
    } else {
      setMessage('Invalid or missing setup token.');
      setMessageType('error');
    }
    
    // If this is for a regular member, redirect to member password setup
    if (userType === 'member') {
      navigate(`/set-password?token=${urlToken}`);
    }
  }, [searchParams, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setMessageType('');
    
    // Validate passwords
    if (!password || !confirmPassword) {
      setMessage('Please enter and confirm your password.');
      setMessageType('error');
      return;
    }
    
    if (password !== confirmPassword) {
      setMessage('Passwords do not match.');
      setMessageType('error');
      return;
    }
    
    if (password.length < 8) {
      setMessage('Password must be at least 8 characters long.');
      setMessageType('error');
      return;
    }
    
    // Check password complexity
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[@$!%*?&]/.test(password);
    
    if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
      setMessage('Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character (@$!%*?&).');
      setMessageType('error');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Determine which endpoint to use based on user type
      const userType = searchParams.get('type') || 'admin';
      let endpoint = `${process.env.REACT_APP_API_URL}/admin/auth/setup-password`;
      
      if (userType === 'member') {
        endpoint = `${process.env.REACT_APP_API_URL}/members/set-password`;
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          password
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setMessage('✅ Password set successfully! Redirecting you to sign in...');
        setMessageType('success');
        // Redirect to login page immediately after showing success message
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setMessage(data.message || 'Failed to set password. Please try again.');
        setMessageType('error');
      }
    } catch (error) {
      console.error('Password setup error:', error);
      setMessage('An error occurred. Please try again later.');
      setMessageType('error');
    }
    
    setIsSubmitting(false);
  };

  const currentYear = new Date().getFullYear();
  const RISSATECH_NOVA_LINK = "https://www.rissatech.com";

  return (
    <PublicLayout>
      <div className="py-5 bg-light">
        <div className="container d-flex align-items-center justify-content-center" style={{ minHeight: '500px' }}>
          <div className="card shadow-lg border-0" style={{ maxWidth: '450px', width: '100%' }}>
            <div className="card-body p-4 p-md-5">
              {/* ARCA Logo */}
              <div className="text-center mb-4">
                <img 
                  src={arcaLogo} 
                  alt="ARCA Logo" 
                  style={{ width: '180px', height: 'auto', marginBottom: '10px' }}
                />
              </div>

              <h2 className="text-center h4 fw-semibold text-dark mb-2">Set Your Password</h2>
              <p className="text-center text-muted mb-4">Create a secure password for your admin account</p>

              {/* Message display for success or error */}
              {message && (
                <div className={`alert ${messageType === 'success' ? 'alert-success' : 'alert-danger'}`} role="alert">
                  <div className="d-flex align-items-center">
                    {messageType === 'success' && (
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Redirecting...</span>
                      </div>
                    )}
                    <div>{message}</div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* Password input field */}
                <div className="mb-3">
                  <label htmlFor="password" className="form-label fw-medium">New Password</label>
                  <div className="position-relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      className="form-control form-control-lg"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder="Enter your new password"
                      aria-label="New Password"
                    />
                    <button
                      type="button"
                      className="btn btn-link position-absolute top-50 end-0 translate-middle-y"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ textDecoration: 'none', color: '#6B8E23' }}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                    </button>
                  </div>
                  <small className="form-text text-muted">
                    Must be at least 8 characters with uppercase, lowercase, number, and special character.
                  </small>
                </div>

                {/* Confirm Password input field */}
                <div className="mb-4">
                  <label htmlFor="confirmPassword" className="form-label fw-medium">Confirm Password</label>
                  <div className="position-relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      className="form-control form-control-lg"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      placeholder="Confirm your new password"
                      aria-label="Confirm Password"
                    />
                    <button
                      type="button"
                      className="btn btn-link position-absolute top-50 end-0 translate-middle-y"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      style={{ textDecoration: 'none', color: '#6B8E23' }}
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                    </button>
                  </div>
                </div>

                {/* Set Password button */}
                <div className="d-grid mb-3">
                  <button
                    type="submit"
                    className="btn btn-success btn-lg"
                    style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Setting Password...
                      </>
                    ) : (
                      'Set Password'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default PasswordSetup;